/**
 * Unique visual for region PostCards.
 * Prefer real region photos; otherwise country photo + unique wash (never a bare
 * shared hero), else a vivid SVG placeholder.
 */
import { getRegionPhoto } from "../data/region-heroes";
import { getCountryHero } from "../data/country-heroes";

function hashSeed(text: string): number {
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function hsl(h: number, s: number, l: number, a = 1): string {
  return a < 1
    ? `hsla(${h % 360}, ${s}%, ${l}%, ${a})`
    : `hsl(${h % 360}, ${s}%, ${l}%)`;
}

export type RegionCardVisual = {
  src: string;
  /** Extra color wash when src is a shared country photo */
  wash?: string;
};

/**
 * Deterministic landscape-ish SVG unique per country/region.
 * Used only when no photo exists yet.
 */
export function regionPlaceholderDataUri(
  countrySlug: string,
  regionSlug: string
): string {
  const seed = hashSeed(`${countrySlug}/${regionSlug}`);
  const hue = 25 + (seed % 140); // sand → olive → teal → blue
  const hue2 = (hue + 32 + ((seed >>> 8) % 45)) % 360;
  const hue3 = (hue + 160 + ((seed >>> 16) % 55)) % 360;
  const c1 = hsl(hue, 48 + (seed % 20), 38 + ((seed >>> 4) % 14));
  const c2 = hsl(hue2, 42 + ((seed >>> 6) % 22), 52 + ((seed >>> 10) % 16));
  const c3 = hsl(hue3, 40 + ((seed >>> 12) % 18), 26 + ((seed >>> 14) % 12));
  const y1 = 48 + ((seed >>> 3) % 22);
  const y2 = 62 + ((seed >>> 5) % 18);
  const peak = 18 + ((seed >>> 7) % 28);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 800" width="640" height="800">
<defs>
  <linearGradient id="g" x1="0" y1="0" x2="0.35" y2="1">
    <stop offset="0%" stop-color="${c2}"/>
    <stop offset="45%" stop-color="${c1}"/>
    <stop offset="100%" stop-color="${c3}"/>
  </linearGradient>
</defs>
<rect width="640" height="800" fill="url(%23g)"/>
<path d="M0 ${y1 + 40} L${120 + (seed % 80)} ${peak + 80} L${260 + ((seed >>> 2) % 90)} ${y1} L${420 + ((seed >>> 4) % 70)} ${peak + 40} L640 ${y1 + 20} L640 800 L0 800 Z" fill="${c3}" opacity="0.65"/>
<path d="M0 ${y2 + 30} L${180 + ((seed >>> 1) % 100)} ${y2 - 10} L${380 + ((seed >>> 3) % 80)} ${y2 + 40} L640 ${y2} L640 800 L0 800 Z" fill="${c1}" opacity="0.8"/>
<circle cx="${480 + ((seed >>> 9) % 100)}" cy="${90 + ((seed >>> 11) % 60)}" r="${36 + ((seed >>> 13) % 28)}" fill="${c2}" opacity="0.45"/>
</svg>`;
  // base64 is more reliable inside CSS url() than utf8 data URIs
  const b64 =
    typeof Buffer !== "undefined"
      ? Buffer.from(svg, "utf8").toString("base64")
      : btoa(unescape(encodeURIComponent(svg)));
  return `data:image/svg+xml;base64,${b64}`;
}

/** Unique translucent wash so a shared country photo still reads as this region. */
export function regionCardWash(
  countrySlug: string,
  regionSlug: string
): string {
  const seed = hashSeed(`${countrySlug}/${regionSlug}/wash`);
  const seed2 = hashSeed(`${regionSlug}/${countrySlug}/tint`);
  const hue = seed % 360;
  const hue2 = (hue + 28 + (seed2 % 80)) % 360;
  const sat = 42 + (seed2 % 28);
  const light = 28 + ((seed >>> 10) % 22);
  const light2 = 32 + ((seed2 >>> 6) % 24);
  const a1 = 0.42 + ((seed >>> 4) % 20) / 100;
  const a2 = 0.22 + ((seed2 >>> 3) % 22) / 100;
  return `linear-gradient(${125 + (seed % 60)}deg, ${hsl(
    hue,
    sat,
    light,
    a1
  )} 0%, ${hsl(hue2, sat - 8, light2, a2)} 45%, ${hsl(
    (hue + hue2) % 360,
    sat - 12,
    Math.max(18, light - 8),
    0.15 + ((seed2 >>> 12) % 15) / 100
  )} 100%)`;
}

export function isSharedCountryImage(
  src: string | null | undefined,
  countrySlug: string
): boolean {
  if (!src) return true;
  const c = (countrySlug || "").toLowerCase();
  const hero = getCountryHero(c);
  if (hero && src === hero) return true;
  if (c && src.includes(`/images/countries/${c}`)) return true;
  if (src.includes("/images/countries/_default")) return true;
  return false;
}

/**
 * Unique image for a highlight card — skip bare country-wide seed heroes.
 */
export function getHighlightCardImage(opts: {
  country?: string | null;
  regionSlug?: string | null;
  image?: string | null;
  slug?: string | null;
}): string {
  return getRegionCardVisual(
    opts.country || "",
    opts.regionSlug || opts.slug || "place",
    opts.image ? [opts.image] : []
  ).src;
}

/**
 * Best unique visual for a region card.
 */
export function getRegionCardVisual(
  countrySlug: string,
  regionSlug: string,
  highlightImages?: Array<string | null | undefined>
): RegionCardVisual {
  const c = (countrySlug || "").toLowerCase();
  const r = (regionSlug || "").toLowerCase().replace(/_/g, "-");
  if (!c || !r) {
    return { src: regionPlaceholderDataUri(c || "world", r || "region") };
  }

  const seeded = getRegionPhoto(c, r);
  if (seeded && !isSharedCountryImage(seeded, c)) {
    return { src: seeded };
  }

  for (const img of highlightImages || []) {
    if (img && !isSharedCountryImage(img, c)) {
      return { src: img };
    }
  }

  // Real country photo + unique wash beats empty / SVG data-URIs (often invisible
  // under card scrims). Wash keeps each canton visually distinct.
  const hero = getCountryHero(c);
  if (hero) {
    return { src: hero, wash: regionCardWash(c, r) };
  }

  return { src: regionPlaceholderDataUri(c, r) };
}

/** @deprecated prefer getRegionCardVisual */
export function getRegionCardImage(
  countrySlug: string,
  regionSlug: string,
  highlightImages?: Array<string | null | undefined>
): string {
  return getRegionCardVisual(countrySlug, regionSlug, highlightImages).src;
}
